import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  X, 
  ShoppingCart, 
  Star, 
  Truck, 
  Shield, 
  Award, 
  Download,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Product } from '../types/product';

interface ProductDetailProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onQuote: (productName: string, productId: number) => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ 
  product, 
  isOpen, 
  onClose, 
  onQuote 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!product) return null;

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.image];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleQuote = () => {
    onQuote(product.name, product.id);
    onClose();
  };

  const features = [
    {
      icon: Truck,
      title: 'Entrega Rápida',
      description: 'Despacho en 24-48 horas para productos en stock'
    },
    {
      icon: Shield,
      title: 'Garantía Oficial',
      description: 'Todos nuestros productos tienen garantía del fabricante'
    },
    {
      icon: Award,
      title: 'Calidad Certificada',
      description: 'Productos con certificaciones internacionales'
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {product.name}
          </DialogTitle>
          <DialogDescription className="text-lg text-gray-600">
            {product.category}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Imágenes del producto */}
          <div className="space-y-4">
            <div className="relative">
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className="w-full h-80 object-contain rounded-lg bg-gray-100"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {product.featured && (
                <div className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  DESTACADO
                </div>
              )}
              {!product.inStock && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                  <div className="bg-red-500 text-white px-4 py-2 rounded-lg font-bold">
                    AGOTADO
                  </div>
                </div>
              )}
            </div>
            
            {/* Miniaturas de imágenes */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      currentImageIndex === index 
                        ? 'border-primary-500' 
                        : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Información del producto */}
          <div className="space-y-6">
            {/* Descripción */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Descripción</h3>
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Rating y reseñas */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(product.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {product.rating} ({product.reviews} reseñas)
              </span>
            </div>

            {/* Precio */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-primary-600">
                  ${product.price.toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">+ IGV</div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                product.inStock 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {product.inStock ? 'En Stock' : 'Agotado'}
              </div>
            </div>

            {/* Especificaciones */}
            {product.specifications && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Especificaciones</h3>
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      {product.specifications.brand && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Marca:</span>
                          <p className="text-gray-900">{product.specifications.brand}</p>
                        </div>
                      )}
                      {product.specifications.model && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Modelo:</span>
                          <p className="text-gray-900">{product.specifications.model}</p>
                        </div>
                      )}
                      {product.specifications.material && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Material:</span>
                          <p className="text-gray-900">{product.specifications.material}</p>
                        </div>
                      )}
                      {product.specifications.dimensions && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Dimensiones:</span>
                          <p className="text-gray-900">{product.specifications.dimensions}</p>
                        </div>
                      )}
                      {product.specifications.weight && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Peso:</span>
                          <p className="text-gray-900">{product.specifications.weight}</p>
                        </div>
                      )}
                      {product.specifications.warranty && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Garantía:</span>
                          <p className="text-gray-900">{product.specifications.warranty}</p>
                        </div>
                      )}
                      {product.specifications.origin && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Origen:</span>
                          <p className="text-gray-900">{product.specifications.origin}</p>
                        </div>
                      )}
                    </div>
                    
                    {product.specifications.technicalDetails && product.specifications.technicalDetails.length > 0 && (
                      <div className="mt-4">
                        <span className="text-sm font-medium text-gray-500">Detalles Técnicos:</span>
                        <ul className="mt-2 space-y-1">
                          {product.specifications.technicalDetails.map((detail, index) => (
                            <li key={index} className="text-sm text-gray-900 flex items-start">
                              <span className="text-primary-600 mr-2">•</span>
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex gap-3">
              <Button
                className="flex-1 btn-primary"
                disabled={!product.inStock}
                onClick={handleQuote}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {product.inStock ? 'Cotizar' : 'No Disponible'}
              </Button>
              {product.datasheet && (
                <Button variant="outline" asChild>
                  <a
                    href={product.datasheet}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Ficha Técnica
                  </a>
                </Button>
              )}
            </div>

            {/* Características adicionales */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="text-xs text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetail;